import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';

interface License {
  id: string;
  licenseKey: string;
  productId: string;
  productName: string;
  licenseType: 'single' | 'multi' | 'unlimited' | 'subscription';
  maxActivations: number;
  currentActivations: number;
  validFrom: Date;
  validUntil?: Date;
  status: 'active' | 'suspended' | 'expired';
  createdAt: Date;
}

const LicenseManagement: React.FC = () => {
  const { t } = useTranslation();
  const [licenses, setLicenses] = useState<License[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<License | null>(null);
  const [form, setForm] = useState({
    productId: '',
    productName: '',
    licenseType: 'single' as License['licenseType'],
    maxActivations: 1,
    validFrom: new Date().toISOString().split('T')[0],
    validUntil: '',
    generateCount: 1
  });

  useEffect(() => {
    loadLicenses();
  }, []);

  const loadLicenses = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, 'marketplace_licenses'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        validFrom: doc.data().validFrom?.toDate(),
        validUntil: doc.data().validUntil?.toDate(),
        createdAt: doc.data().createdAt?.toDate()
      })) as License[];
      
      setLicenses(data);
    } catch (error) {
      console.error('Error loading licenses:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateLicenseKey = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    const segments = 4;
    const segmentLength = 4;
    let key = '';
    
    for (let i = 0; i < segments; i++) {
      for (let j = 0; j < segmentLength; j++) {
        key += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      if (i < segments - 1) key += '-';
    }
    
    return key;
  };

  const handleGenerate = async () => {
    try {
      const validUntil = form.validUntil ? new Date(form.validUntil) : null;
      
      for (let i = 0; i < form.generateCount; i++) {
        const licenseData = {
          licenseKey: generateLicenseKey(),
          productId: form.productId,
          productName: form.productName,
          licenseType: form.licenseType,
          maxActivations: form.maxActivations,
          currentActivations: 0,
          validFrom: Timestamp.fromDate(new Date(form.validFrom)),
          validUntil: validUntil ? Timestamp.fromDate(validUntil) : null,
          status: 'suspended',
          createdAt: Timestamp.now()
        };
        
        await addDoc(collection(db, 'marketplace_licenses'), licenseData);
      }
      
      setShowModal(false);
      resetForm();
      loadLicenses();
    } catch (error) {
      console.error('Error generating licenses:', error);
    }
  };

  const handleSuspend = async (id: string) => {
    if (window.confirm(t('marketplace.confirmSuspendLicense'))) {
      try {
        await updateDoc(doc(db, 'marketplace_licenses', id), {
          status: 'suspended',
          suspendedAt: Timestamp.now()
        });
        loadLicenses();
      } catch (error) {
        console.error('Error suspending license:', error);
      }
    }
  };

  const handleActivate = async (id: string) => {
    try {
      const license = licenses.find(l => l.id === id);
      if (!license) return;
      
      // Check if max activations reached
      if (license.maxActivations !== -1 && license.currentActivations >= license.maxActivations) {
        alert(t('marketplace.maxActivationsReached'));
        return;
      }
      
      await updateDoc(doc(db, 'marketplace_licenses', id), {
        status: 'active',
        currentActivations: Math.min(license.currentActivations + 1, license.maxActivations === -1 ? license.currentActivations + 1 : license.maxActivations),
        reactivatedAt: Timestamp.now()
      });
      loadLicenses();
    } catch (error) {
      console.error('Error activating license:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm(t('marketplace.confirmDeleteLicense'))) {
      try {
        await deleteDoc(doc(db, 'marketplace_licenses', id));
        loadLicenses();
      } catch (error) {
        console.error('Error deleting license:', error);
      }
    }
  };

  const resetForm = () => {
    setForm({
      productId: '',
      productName: '',
      licenseType: 'single',
      maxActivations: 1,
      validFrom: new Date().toISOString().split('T')[0],
      validUntil: '',
      generateCount: 1
    });
  };

  const getStatusColor = (status: License['status']) => {
    switch (status) {
      case 'active': return 'bg-green-500/20 text-green-400';
      case 'suspended': return 'bg-yellow-500/20 text-yellow-400';
      case 'expired': return 'bg-red-500/20 text-red-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  const getLicenseTypeIcon = (type: License['licenseType']) => {
    switch (type) {
      case 'single': return '🔑';
      case 'multi': return '🔐';
      case 'unlimited': return '♾️';
      case 'subscription': return '📅';
      default: return '🔒';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-[#B8B8C3]">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-2xl font-bold text-white">{t('marketplace.licenseManagement')}</h3>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="btn-primary flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {t('marketplace.generateLicenses')}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {licenses.map(license => (
          <div key={license.id} className="card-dark p-6">
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-3">
                <span className="text-3xl">{getLicenseTypeIcon(license.licenseType)}</span>
                <div>
                  <h4 className="text-lg font-bold text-white font-mono">{license.licenseKey}</h4>
                  <p className="text-xs text-[#B8B8C3]">{license.productName}</p>
                </div>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(license.status)}`}>
                {t(`marketplace.status.${license.status}`)}
              </span>
            </div>

            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-sm">
                <span className="text-[#B8B8C3]">Licenc Típusa:</span>
                <span className="text-white font-semibold">{t(`marketplace.licenseType.${license.licenseType}`)}</span>
              </div>

              <div className="flex justify-between text-sm">
                <span className="text-[#B8B8C3]">{t('marketplace.activations')}:</span>
                <span className="text-cyan-400 font-semibold">
                  {license.currentActivations} / {license.maxActivations === -1 ? '∞' : license.maxActivations}
                </span>
              </div>

              <div className="flex justify-between text-sm">
                <span className="text-[#B8B8C3]">{t('marketplace.validFrom')}:</span>
                <span className="text-white">{license.validFrom.toLocaleDateString()}</span>
              </div>

              {license.validUntil && (
                <div className="flex justify-between text-sm">
                  <span className="text-[#B8B8C3]">{t('marketplace.validUntil')}:</span>
                  <span className="text-white">{license.validUntil.toLocaleDateString()}</span>
                </div>
              )}
            </div>

            <div className="flex gap-2">
              {license.status === 'active' ? (
                <button
                  onClick={() => handleSuspend(license.id)}
                  className="flex-1 px-4 py-2 bg-yellow-500/10 text-yellow-400 rounded-lg hover:bg-yellow-500/20 transition-colors text-sm"
                >
                  {t('marketplace.suspend')}
                </button>
              ) : (
                <button
                  onClick={() => handleActivate(license.id)}
                  className="flex-1 px-4 py-2 bg-green-500/10 text-green-400 rounded-lg hover:bg-green-500/20 transition-colors text-sm"
                >
                  {t('marketplace.activate')}
                </button>
              )}
              <button
                onClick={() => handleDelete(license.id)}
                className="px-4 py-2 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors text-sm"
              >
                {t('common.delete')}
              </button>
            </div>
          </div>
        ))}
      </div>

      {licenses.length === 0 && (
        <div className="card-dark p-12 text-center">
          <div className="text-6xl mb-4">🔑</div>
          <h3 className="text-xl font-bold text-white mb-2">{t('marketplace.noLicenses')}</h3>
          <p className="text-[#B8B8C3] mb-4">{t('marketplace.noLicensesDesc')}</p>
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="btn-primary"
          >
            {t('marketplace.generateFirstLicense')}
          </button>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card-dark p-6 max-w-2xl w-full">
            <h3 className="text-2xl font-bold text-white mb-6">
              {t('marketplace.generateLicenses')}
            </h3>

            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    {t('marketplace.productId')}
                  </label>
                  <input
                    type="text"
                    value={form.productId}
                    onChange={(e) => setForm({ ...form, productId: e.target.value })}
                    className="input-dark w-full"
                    placeholder="prod_123456"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    {t('marketplace.productName')}
                  </label>
                  <input
                    type="text"
                    value={form.productName}
                    onChange={(e) => setForm({ ...form, productName: e.target.value })}
                    className="input-dark w-full"
                    placeholder="Premium Motion Pack"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    Licenc Típusa
                  </label>
                  <select
                    value={form.licenseType}
                    onChange={(e) => setForm({ ...form, licenseType: e.target.value as License['licenseType'] })}
                    className="input-dark w-full"
                  >
                    <option value="single">{t('marketplace.licenseType.single')}</option>
                    <option value="multi">{t('marketplace.licenseType.multi')}</option>
                    <option value="unlimited">{t('marketplace.licenseType.unlimited')}</option>
                    <option value="subscription">{t('marketplace.licenseType.subscription')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    {t('marketplace.maxActivations')}
                  </label>
                  <input
                    type="number"
                    value={form.maxActivations}
                    onChange={(e) => setForm({ ...form, maxActivations: Number(e.target.value) })}
                    className="input-dark w-full"
                    min="1"
                    disabled={form.licenseType === 'unlimited'}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    {t('marketplace.validFrom')}
                  </label>
                  <input
                    type="date"
                    value={form.validFrom}
                    onChange={(e) => setForm({ ...form, validFrom: e.target.value })}
                    max="9999-12-31"
                    className="input-dark w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    {t('marketplace.validUntil')} ({t('marketplace.optional')})
                  </label>
                  <input
                    type="date"
                    value={form.validUntil}
                    onChange={(e) => setForm({ ...form, validUntil: e.target.value })}
                    max="9999-12-31"
                    className="input-dark w-full"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                  {t('marketplace.generateCount')}
                </label>
                <input
                  type="number"
                  value={form.generateCount}
                  onChange={(e) => setForm({ ...form, generateCount: Number(e.target.value) })}
                  className="input-dark w-full"
                  min="1"
                  max="100"
                />
                <p className="text-xs text-[#B8B8C3] mt-1">{t('marketplace.generateCountHint')}</p>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={handleGenerate} className="btn-primary flex-1">
                {t('marketplace.generate')}
              </button>
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="btn-secondary flex-1"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LicenseManagement;
